import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fetchPageCopy } from '../lib/copy';
import { SHOW_CAREERS } from '../lib/features';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

const COPY_DEFAULTS = {
  headline: 'Independent creative studio for commercial film.',
  intro:    "I work directly with brands and agencies, from the first brief through to finished assets, or stepping in wherever the project needs it.",
  rtb:      "Four years as a creative producer at an award-winning commercial film production company.",
  rates:    "Rates vary depending on scope, duration, and the nature of the work. Please get in touch and I can discuss what makes sense.",
};

const DEFAULT_SERVICES = [
  { title: 'Commercial Editing',      desc: "Leading film editing projects from start to finish. Hand me the rushes and I'll bring your story to life." },
  { title: 'Creative Development',    desc: 'Leading creative pre-production from the ground up. Scripts, storyboards and shot lists crafted to sharpen your brief and set production up for success.' },
  { title: 'Production Support',      desc: 'Available to lead or support at any stage of a commercial production, from pre-production through to delivery.' },
  { title: 'Post Production Support', desc: 'Leading post-production efforts in-house or managing third-party post teams, keeping quality and deadlines on track.' },
];

export default function Services() {
  const [copy, setCopy]         = useState(COPY_DEFAULTS);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const isMobile = useIsMobile();
  usePageTitle('What I Offer');

  useEffect(() => {
    fetchPageCopy('services').then(c => {
      if (Object.keys(c).length > 0) setCopy(prev => ({ ...prev, ...c }));
    });
    supabase.from('site_settings').select('value').eq('key', 'services_list').single().then(({ data }) => {
      if (data?.value) setServices(JSON.parse(data.value));
    });
  }, []);

  return (
    <div className="page-fade" style={{ paddingTop: '56px' }}>
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.5rem 5rem' : '5rem 3rem 8rem' }}>

        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '2rem' : '6rem',
          marginBottom: isMobile ? '3.5rem' : '6rem',
          paddingBottom: isMobile ? '3.5rem' : '6rem',
          borderBottom: '1px solid var(--rule)',
        }}>
          <div>
            <p style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '2rem' }}>
              What I Offer
            </p>
            <h1 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 300, lineHeight: 1.15, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              {copy.headline}
            </h1>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '1.5rem' }}>
            <p style={{ fontSize: '14px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.85 }}>
              {copy.intro}
            </p>
            <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--ink-faint)', lineHeight: 1.7, borderLeft: '2px solid var(--rule)', paddingLeft: '1rem' }}>
              {copy.rtb}
            </p>
          </div>
        </div>

        {/* Services list */}
        <div>
          {services.map(({ title, desc }) => (
            <div key={title} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? '0.75rem' : '6rem',
              padding: isMobile ? '2rem 0' : '2.5rem 0',
              borderBottom: '1px solid var(--rule)',
              alignItems: 'start',
            }}>
              <p style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: 300, color: 'var(--ink)', lineHeight: 1.25, letterSpacing: '-0.01em' }}>
                {title}
              </p>
              <p style={{ fontSize: '14px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.85 }}>
                {desc}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '1.5rem' : '6rem',
          paddingTop: isMobile ? '3rem' : '5rem',
          alignItems: 'end',
        }}>
          <p style={{ fontSize: '14px', fontWeight: 300, color: 'var(--ink-faint)', lineHeight: 1.8 }}>
            {copy.rates}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
            <a href="mailto:liam@liamboyd.com" style={{ fontSize: '15px', fontWeight: 300, color: 'var(--ink)', borderBottom: '1px solid var(--ink)', paddingBottom: '2px', transition: 'opacity 150ms' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.5'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              liam@liamboyd.com
            </a>
            {SHOW_CAREERS && (
              <Link to="/careers" style={{ fontSize: '12px', fontWeight: 300, color: 'var(--ink-faint)', letterSpacing: '0.04em' }}>
                View career history →
              </Link>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}
